import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Calendar, MapPin, Users, Plus, Search, X, Clock, User, Check, Filter, Lock, Globe, UserPlus, UsersIcon, Pencil } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import AddressAutocomplete from '../AddressAutocomplete';
import {
    getEvents,
    createEvent,
    updateEvent,
    joinEvent,
    leaveEvent,
    getEventParticipants,
    addEventParticipant,
    addEventGroup,
    EUROPEAN_COUNTRIES
} from '../../services/eventsService';
import { getUserContacts } from '../../services/userContactsService';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for events
const eventIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

/**
 * Events Module
 * List and map of public events from all members
 */
const Events = () => {
    const { t } = useTranslation();
    const { language } = useLanguage();

    // Current user
    const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10);

    // State
    const [myEvents, setMyEvents] = useState([]);
    const [invitedEvents, setInvitedEvents] = useState([]);
    const [publicEvents, setPublicEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showParticipants, setShowParticipants] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [activeTab, setActiveTab] = useState('my'); // 'my', 'invited', 'public'
    const [editingEvent, setEditingEvent] = useState(null); // Event being edited

    // Map center (Europe)
    const mapCenter = [50.8503, 4.3517]; // Brussels as default center

    // Load all event categories
    useEffect(() => {
        loadAllEvents();
    }, [selectedCountry, searchKeyword, currentUserId]);

    const loadAllEvents = async () => {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];

        // Load all three categories in parallel
        const [myResult, invitedResult, publicResult] = await Promise.all([
            // My events (where I participate)
            getEvents({
                countryCode: selectedCountry,
                keyword: searchKeyword,
                startDate: today,
                viewerId: currentUserId,
                onlyMyEvents: true,
                limit: 100
            }),
            // Invited events (private events where I'm invited but not creator)
            getEvents({
                countryCode: selectedCountry,
                keyword: searchKeyword,
                startDate: today,
                viewerId: currentUserId,
                onlyInvited: true,
                limit: 100
            }),
            // Public events
            getEvents({
                countryCode: selectedCountry,
                keyword: searchKeyword,
                startDate: today,
                viewerId: currentUserId,
                onlyPublic: true,
                limit: 100
            })
        ]);

        setMyEvents(myResult.events);
        setInvitedEvents(invitedResult.events);
        setPublicEvents(publicResult.events);
        setLoading(false);
    };

    // Get current events list based on active tab
    const getCurrentEvents = () => {
        switch (activeTab) {
            case 'my': return myEvents;
            case 'invited': return invitedEvents;
            case 'public': return publicEvents;
            default: return [];
        }
    };

    // Get all events for map display
    const getAllEventsForMap = () => {
        const allEvents = [...myEvents, ...invitedEvents, ...publicEvents];
        // Remove duplicates by id
        const uniqueEvents = allEvents.filter((event, index, self) =>
            index === self.findIndex(e => e.id === event.id)
        );
        return uniqueEvents;
    };

    const handleJoinEvent = async (event) => {
        const result = await joinEvent(event.id, currentUserId);
        if (result.success) {
            loadAllEvents();
        }
    };

    const handleLeaveEvent = async (event) => {
        const result = await leaveEvent(event.id, currentUserId);
        if (result.success) {
            loadAllEvents();
        }
    };

    const handleShowParticipants = async (event) => {
        setSelectedEvent(event);
        const participantsList = await getEventParticipants(event.id);
        setParticipants(participantsList);
        setShowParticipants(true);
    };

    const isCreator = (event) => event.creatorId === currentUserId;

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const events = getCurrentEvents();

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                <h1 className="text-2xl font-bold text-gray-800">
                    {language === 'fr' ? 'Événements' : 'Events'}
                </h1>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                    <Plus className="w-5 h-5" />
                    {language === 'fr' ? 'Créer un événement' : 'Create Event'}
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto">
                <button
                    onClick={() => setActiveTab('my')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                        activeTab === 'my'
                            ? 'bg-linkoo-purple text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <Calendar className="w-4 h-4" />
                    {language === 'fr' ? 'Mes événements' : 'My Events'}
                    {myEvents.length > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            activeTab === 'my' ? 'bg-white/20' : 'bg-linkoo-purple/20 text-linkoo-purple'
                        }`}>
                            {myEvents.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('invited')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                        activeTab === 'invited'
                            ? 'bg-linkoo-purple text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <UserPlus className="w-4 h-4" />
                    {language === 'fr' ? 'Invitations' : 'Invitations'}
                    {invitedEvents.length > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            activeTab === 'invited' ? 'bg-white/20' : 'bg-orange-500/20 text-orange-600'
                        }`}>
                            {invitedEvents.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('public')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                        activeTab === 'public'
                            ? 'bg-linkoo-purple text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <Globe className="w-4 h-4" />
                    {language === 'fr' ? 'Événements publics' : 'Public Events'}
                    {publicEvents.length > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            activeTab === 'public' ? 'bg-white/20' : 'bg-green-500/20 text-green-600'
                        }`}>
                            {publicEvents.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Country filter */}
                <div className="relative flex-1">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                    >
                        <option value="">{language === 'fr' ? 'Tous les pays' : 'All countries'}</option>
                        {EUROPEAN_COUNTRIES.map(country => (
                            <option key={country.code} value={country.code}>
                                {language === 'fr' ? country.name : country.nameEn}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder={language === 'fr' ? 'Rechercher par nom, lieu...' : 'Search by name, place...'}
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                    />
                </div>
            </div>

            {/* Content: Map + List */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0">
                {/* Map */}
                <div className="h-[300px] lg:h-full rounded-xl overflow-hidden shadow-md">
                    <MapContainer
                        center={mapCenter}
                        zoom={5}
                        style={{ height: '100%', width: '100%' }}
                        scrollWheelZoom={true}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {getAllEventsForMap().filter(e => e.latitude && e.longitude).map(event => (
                            <Marker
                                key={event.id}
                                position={[event.latitude, event.longitude]}
                                icon={eventIcon}
                            >
                                <Popup>
                                    <div className="min-w-[200px]">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-gray-800">{event.title}</h3>
                                            {!event.isPublic && <Lock className="w-3 h-3 text-orange-500" />}
                                        </div>
                                        <p className="text-sm text-gray-600">{formatDate(event.eventDate)}</p>
                                        {event.startTime && (
                                            <p className="text-sm text-gray-500">{event.startTime}</p>
                                        )}
                                        <p className="text-sm text-linkoo-purple mt-1">{event.placeName}</p>
                                        <p className="text-xs text-gray-500">{event.placeAddress}</p>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>

                {/* Events List */}
                <div className="h-[400px] lg:h-full overflow-y-auto space-y-3 pr-2">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-linkoo-purple"></div>
                        </div>
                    ) : events.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                            <Calendar className="w-12 h-12 mb-2 opacity-50" />
                            <p>
                                {activeTab === 'my' && (language === 'fr' ? 'Vous ne participez à aucun événement' : 'You are not participating in any event')}
                                {activeTab === 'invited' && (language === 'fr' ? 'Aucune invitation' : 'No invitations')}
                                {activeTab === 'public' && (language === 'fr' ? 'Aucun événement public' : 'No public events')}
                            </p>
                        </div>
                    ) : (
                        events.map(event => (
                            <div
                                key={event.id}
                                className={`bg-white rounded-xl p-4 shadow-sm border transition-shadow hover:shadow-md ${
                                    !event.isPublic ? 'border-orange-200' : 'border-gray-100'
                                }`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-gray-800 text-lg">{event.title}</h3>
                                        {!event.isPublic && (
                                            <span className="flex items-center gap-1 bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded-full">
                                                <Lock className="w-3 h-3" />
                                                {language === 'fr' ? 'Privé' : 'Private'}
                                            </span>
                                        )}
                                    </div>
                                    {isCreator(event) && (
                                        <div className="flex items-center gap-2">
                                            <span className="bg-linkoo-purple/10 text-linkoo-purple text-xs px-2 py-1 rounded-full">
                                                {language === 'fr' ? 'Organisateur' : 'Organizer'}
                                            </span>
                                            <button
                                                onClick={() => setEditingEvent(event)}
                                                className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                                                title={language === 'fr' ? 'Modifier' : 'Edit'}
                                            >
                                                <Pencil className="w-3.5 h-3.5 text-gray-600" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-1 text-sm text-gray-600 mb-3">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-linkoo-purple" />
                                        <span>{formatDate(event.eventDate)}</span>
                                    </div>
                                    {event.startTime && (
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-linkoo-purple" />
                                            <span>{event.startTime}{event.endTime ? ` - ${event.endTime}` : ''}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-linkoo-purple" />
                                        <span>{event.placeName}</span>
                                    </div>
                                    <p className="text-xs text-gray-400 ml-6">{event.placeAddress}</p>
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-gray-400" />
                                        <span className="text-gray-500">
                                            {event.creatorFirstName} {event.creatorLastName}
                                        </span>
                                    </div>
                                </div>

                                {event.description && (
                                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{event.description}</p>
                                )}

                                <div className="flex items-center justify-between">
                                    <button
                                        onClick={() => handleShowParticipants(event)}
                                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-linkoo-purple transition-colors"
                                    >
                                        <Users className="w-4 h-4" />
                                        <span>
                                            {event.participantCount} {language === 'fr' ? 'participant(s)' : 'participant(s)'}
                                        </span>
                                    </button>

                                    {!isCreator(event) && (
                                        event.isParticipating || event.myParticipationStatus === 'confirmed' ? (
                                            <button
                                                onClick={() => handleLeaveEvent(event)}
                                                className="flex items-center gap-1 text-sm bg-green-100 text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-200 transition-colors"
                                            >
                                                <Check className="w-4 h-4" />
                                                {language === 'fr' ? 'Inscrit' : 'Joined'}
                                            </button>
                                        ) : event.myParticipationStatus === 'invited' ? (
                                            <button
                                                onClick={() => handleJoinEvent(event)}
                                                className="flex items-center gap-1 text-sm bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors"
                                            >
                                                <Check className="w-4 h-4" />
                                                {language === 'fr' ? 'Accepter' : 'Accept'}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleJoinEvent(event)}
                                                className="flex items-center gap-1 text-sm bg-linkoo-purple text-white px-3 py-1.5 rounded-lg hover:bg-linkoo-purple/90 transition-colors"
                                            >
                                                <Plus className="w-4 h-4" />
                                                {language === 'fr' ? 'Participer' : 'Join'}
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Create Event Modal */}
            {showCreateModal && (
                <CreateEventModal
                    onClose={() => setShowCreateModal(false)}
                    onEventCreated={() => {
                        setShowCreateModal(false);
                        loadAllEvents();
                    }}
                    currentUserId={currentUserId}
                    language={language}
                />
            )}

            {/* Edit Event Modal */}
            {editingEvent && (
                <EditEventModal
                    event={editingEvent}
                    onClose={() => setEditingEvent(null)}
                    onEventUpdated={() => {
                        setEditingEvent(null);
                        loadAllEvents();
                    }}
                    currentUserId={currentUserId}
                    language={language}
                />
            )}

            {/* Participants Modal */}
            {showParticipants && selectedEvent && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">
                                {language === 'fr' ? 'Participants' : 'Participants'}
                            </h2>
                            <button
                                onClick={() => setShowParticipants(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <h3 className="text-lg font-medium text-linkoo-purple mb-4">{selectedEvent.title}</h3>

                        {participants.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">
                                {language === 'fr' ? 'Aucun participant pour le moment' : 'No participants yet'}
                            </p>
                        ) : (
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {participants.map(p => (
                                    <div key={p.id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-linkoo-purple/20 rounded-full flex items-center justify-center">
                                                <User className="w-4 h-4 text-linkoo-purple" />
                                            </div>
                                            <span className="font-medium text-gray-800">
                                                {p.firstName} {p.lastName}
                                            </span>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            p.status === 'confirmed' ? 'bg-green-100 text-green-600' :
                                            p.status === 'invited' ? 'bg-orange-100 text-orange-600' :
                                            p.status === 'declined' ? 'bg-red-100 text-red-600' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {p.status === 'confirmed' && (language === 'fr' ? 'Confirmé' : 'Confirmed')}
                                            {p.status === 'invited' && (language === 'fr' ? 'Invité' : 'Invited')}
                                            {p.status === 'declined' && (language === 'fr' ? 'Décliné' : 'Declined')}
                                            {p.status === 'maybe' && (language === 'fr' ? 'Peut-être' : 'Maybe')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Create Event Modal Component
 */
const CreateEventModal = ({ onClose, onEventCreated, currentUserId, language }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eventDate: '',
        startTime: '',
        endTime: '',
        placeName: '',
        placeAddress: '',
        latitude: null,
        longitude: null,
        country: '',
        countryCode: '',
        maxParticipants: 0,
        isPublic: true
    });
    const [saving, setSaving] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);

    // Load contacts when switching to private
    useEffect(() => {
        if (!formData.isPublic && contacts.length === 0) {
            loadContacts();
        }
    }, [formData.isPublic]);

    const loadContacts = async () => {
        setLoadingContacts(true);
        try {
            const userContacts = await getUserContacts();
            // Filter only contacts with a peopleId (linked to Linkoo profiles)
            const linkooContacts = userContacts.filter(c => c.peopleid || c.peopleId);
            setContacts(linkooContacts);
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
        setLoadingContacts(false);
    };

    const toggleContact = (contact) => {
        const contactId = contact.peopleid || contact.peopleId;
        if (selectedContacts.includes(contactId)) {
            setSelectedContacts(prev => prev.filter(id => id !== contactId));
        } else {
            setSelectedContacts(prev => [...prev, contactId]);
        }
    };

    // Handle address change from AddressAutocomplete
    const handleAddressChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            placeAddress: value,
            placeName: value
        }));
    };

    // Handle coordinates from AddressAutocomplete
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
        }));
    };

    // Handle address components from AddressAutocomplete
    const handleAddressComponentsChange = (components) => {
        setFormData(prev => ({
            ...prev,
            country: components.country || '',
            countryCode: components.countryCode || ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.eventDate) return;

        setSaving(true);

        // Create event with isPublic flag
        const result = await createEvent({
            ...formData,
            isPublic: formData.isPublic
        }, currentUserId);

        if (result.success) {
            // If private event and contacts selected, invite them
            if (!formData.isPublic && selectedContacts.length > 0 && result.eventId) {
                // Add each selected contact as invited participant
                for (const peopleId of selectedContacts) {
                    try {
                        await addEventParticipant(result.eventId, peopleId, 'invited');
                    } catch (err) {
                        console.error('Error inviting contact:', err);
                    }
                }
            }
            onEventCreated();
        } else {
            alert(result.message || 'Error creating event');
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto overflow-x-visible">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center z-10">
                    <h2 className="text-xl font-bold text-gray-800">
                        {language === 'fr' ? 'Créer un événement' : 'Create Event'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* Public/Private Toggle */}
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                                formData.isPublic
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <Globe className="w-5 h-5" />
                            {language === 'fr' ? 'Public' : 'Public'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                                !formData.isPublic
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <Lock className="w-5 h-5" />
                            {language === 'fr' ? 'Privé' : 'Private'}
                        </button>
                    </div>

                    {formData.isPublic ? (
                        <p className="text-sm text-gray-500 bg-green-50 p-2 rounded-lg">
                            {language === 'fr'
                                ? 'Visible par tous les membres Linkoo'
                                : 'Visible to all Linkoo members'}
                        </p>
                    ) : (
                        <p className="text-sm text-gray-500 bg-orange-50 p-2 rounded-lg">
                            {language === 'fr'
                                ? 'Visible uniquement par les contacts invités'
                                : 'Visible only to invited contacts'}
                        </p>
                    )}

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Titre *' : 'Title *'}
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            required
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Description' : 'Description'}
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Date *' : 'Date *'}
                        </label>
                        <input
                            type="date"
                            value={formData.eventDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            required
                        />
                    </div>

                    {/* Time */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Heure de début' : 'Start Time'}
                            </label>
                            <input
                                type="time"
                                value={formData.startTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Heure de fin' : 'End Time'}
                            </label>
                            <input
                                type="time"
                                value={formData.endTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Place Search using AddressAutocomplete */}
                    <AddressAutocomplete
                        label={language === 'fr' ? 'Lieu (rechercher un établissement ou une adresse)' : 'Place (search for a venue or address)'}
                        value={formData.placeAddress}
                        onChange={handleAddressChange}
                        onCoordinatesChange={handleCoordinatesChange}
                        onAddressComponentsChange={handleAddressComponentsChange}
                    />

                    {/* Selected Place Info */}
                    {formData.placeAddress && formData.latitude && (
                        <div className="bg-linkoo-purple/5 border border-linkoo-purple/20 rounded-lg p-3">
                            <p className="text-sm text-gray-600">{formData.placeAddress}</p>
                            {formData.country && (
                                <p className="text-xs text-gray-500 mt-1">{formData.country}</p>
                            )}
                        </div>
                    )}

                    {/* Max Participants */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Nombre maximum de participants (0 = illimité)' : 'Max participants (0 = unlimited)'}
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={formData.maxParticipants}
                            onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                        />
                    </div>

                    {/* Invite Contacts (only for private events) */}
                    {!formData.isPublic && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <div className="flex items-center gap-2">
                                    <UserPlus className="w-4 h-4" />
                                    {language === 'fr' ? 'Inviter des contacts' : 'Invite contacts'}
                                </div>
                            </label>

                            {loadingContacts ? (
                                <div className="flex items-center justify-center py-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-linkoo-purple"></div>
                                </div>
                            ) : contacts.length === 0 ? (
                                <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                                    {language === 'fr'
                                        ? 'Aucun contact Linkoo à inviter. Seuls les contacts liés à un profil Linkoo peuvent être invités.'
                                        : 'No Linkoo contacts to invite. Only contacts linked to a Linkoo profile can be invited.'}
                                </p>
                            ) : (
                                <div className="max-h-40 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                                    {contacts.map(contact => {
                                        const contactId = contact.peopleid || contact.peopleId;
                                        const isSelected = selectedContacts.includes(contactId);
                                        return (
                                            <button
                                                key={contact.localuuid || contactId}
                                                type="button"
                                                onClick={() => toggleContact(contact)}
                                                className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                                    isSelected
                                                        ? 'bg-linkoo-purple/10 border border-linkoo-purple/30'
                                                        : 'bg-gray-50 hover:bg-gray-100'
                                                }`}
                                            >
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                    isSelected ? 'bg-linkoo-purple text-white' : 'bg-gray-200'
                                                }`}>
                                                    {isSelected ? <Check className="w-4 h-4" /> : <User className="w-3 h-3" />}
                                                </div>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {contact.firstname || contact.firstName} {contact.lastname || contact.lastName}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {selectedContacts.length > 0 && (
                                <p className="text-xs text-linkoo-purple mt-2">
                                    {selectedContacts.length} {language === 'fr' ? 'contact(s) sélectionné(s)' : 'contact(s) selected'}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={saving || !formData.title || !formData.eventDate}
                        className="w-full py-3 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {language === 'fr' ? 'Création...' : 'Creating...'}
                            </span>
                        ) : (
                            language === 'fr' ? 'Créer l\'événement' : 'Create Event'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

/**
 * Edit Event Modal Component
 */
const EditEventModal = ({ event, onClose, onEventUpdated, currentUserId, language }) => {
    const [formData, setFormData] = useState({
        title: event.title || '',
        description: event.description || '',
        eventDate: event.eventDate || '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        placeName: event.placeName || '',
        placeAddress: event.placeAddress || '',
        latitude: event.latitude || null,
        longitude: event.longitude || null,
        country: event.country || '',
        countryCode: event.countryCode || '',
        maxParticipants: event.maxParticipants || 0,
        isPublic: event.isPublic !== false // Default to true if undefined
    });
    const [saving, setSaving] = useState(false);

    // Handle address change from AddressAutocomplete
    const handleAddressChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            placeAddress: value,
            placeName: value
        }));
    };

    // Handle coordinates from AddressAutocomplete
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
        }));
    };

    // Handle address components from AddressAutocomplete
    const handleAddressComponentsChange = (components) => {
        setFormData(prev => ({
            ...prev,
            country: components.country || '',
            countryCode: components.countryCode || ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.eventDate) return;

        setSaving(true);

        const result = await updateEvent(event.eventUuid, {
            ...formData,
            creatorId: currentUserId
        });

        if (result.success) {
            onEventUpdated();
        } else {
            alert(result.message || 'Error updating event');
        }
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto overflow-x-visible">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center z-10">
                    <h2 className="text-xl font-bold text-gray-800">
                        {language === 'fr' ? 'Modifier l\'événement' : 'Edit Event'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* Public/Private Toggle */}
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                                formData.isPublic
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <Globe className="w-5 h-5" />
                            {language === 'fr' ? 'Public' : 'Public'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                                !formData.isPublic
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <Lock className="w-5 h-5" />
                            {language === 'fr' ? 'Privé' : 'Private'}
                        </button>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Titre *' : 'Title *'}
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            required
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Description' : 'Description'}
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Date *' : 'Date *'}
                        </label>
                        <input
                            type="date"
                            value={formData.eventDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            required
                        />
                    </div>

                    {/* Time */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Heure de début' : 'Start Time'}
                            </label>
                            <input
                                type="time"
                                value={formData.startTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {language === 'fr' ? 'Heure de fin' : 'End Time'}
                            </label>
                            <input
                                type="time"
                                value={formData.endTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Place Search using AddressAutocomplete */}
                    <AddressAutocomplete
                        label={language === 'fr' ? 'Lieu (rechercher un établissement ou une adresse)' : 'Place (search for a venue or address)'}
                        value={formData.placeAddress}
                        onChange={handleAddressChange}
                        onCoordinatesChange={handleCoordinatesChange}
                        onAddressComponentsChange={handleAddressComponentsChange}
                    />

                    {/* Selected Place Info */}
                    {formData.placeAddress && formData.latitude && (
                        <div className="bg-linkoo-purple/5 border border-linkoo-purple/20 rounded-lg p-3">
                            <p className="text-sm text-gray-600">{formData.placeAddress}</p>
                            {formData.country && (
                                <p className="text-xs text-gray-500 mt-1">{formData.country}</p>
                            )}
                        </div>
                    )}

                    {/* Max Participants */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Nombre maximum de participants (0 = illimité)' : 'Max participants (0 = unlimited)'}
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={formData.maxParticipants}
                            onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linkoo-purple focus:border-transparent"
                        />
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={saving || !formData.title || !formData.eventDate}
                        className="w-full py-3 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {language === 'fr' ? 'Enregistrement...' : 'Saving...'}
                            </span>
                        ) : (
                            language === 'fr' ? 'Enregistrer les modifications' : 'Save Changes'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Events;
